import { cx } from '@emotion/css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import { useCallback, useRef, useState } from 'react';
import { ActionButtons } from '../ActionButtons';
import { AlertBanner } from '../AlertBanner';
import type { AlertBannerProps } from '../AlertBanner/AlertBanner.model';
import type { BDAlert } from '../BDAlerts';
import { BDAlerts } from '../BDAlerts';
import { BDBottomSheet } from '../BDBottomSheet';
import { Flex } from '../Flex';
import { IconButton } from '../IconButton';
import type {
  DialogTitleProps,
  DialogWrapperProps,
  ModalCloseReason,
} from './BDDialog.model';
import useStyles from './BDDialog.styles';

const DialogTitle = (props: DialogTitleProps) => {
  const { onClose, title, disableClose, extendedMobile, ...other } = props;
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles();
  return (
    <MuiDialogTitle {...other} variant="header1_medium">
      {extendedMobile ? (
        <Grid container>
          {isMobileView && !disableClose && (
            <Grid item xs={1.5}>
              <IconButton
                aria-label={props.labels?.close}
                className={classes.dialogClose}
                onClick={onClose}
                size="small"
                data-testid={`${props.id}-close-button`}
                icon={<ArrowBackIcon data-testid="close-dialog-arrow" />}
                variant="fill"
              />
            </Grid>
          )}

          <Grid item xs={disableClose ? 12 : 9} md={10.5}>
            <Flex center={isMobileView}>{title}</Flex>
          </Grid>

          {!isMobileView && !disableClose && (
            <Grid item xs={1.5}>
              <Flex className={classes.rightClose}>
                {!disableClose && (
                  <IconButton
                    aria-label={props.labels?.close}
                    className={classes.dialogClose}
                    onClick={onClose}
                    size="small"
                    data-testid={`${props.id}-close-button`}
                    icon={<CloseIcon data-testid="x-close-icon" />}
                    variant="fill"
                  />
                )}
              </Flex>
            </Grid>
          )}
        </Grid>
      ) : (
        <>
          {title}
          <div className={classes.closeWrapper}>
            {!disableClose ? (
              <IconButton
                aria-label={props.labels?.close}
                className={classes.dialogClose}
                onClick={onClose}
                size="small"
                data-testid={`${props.id}-close-button`}
                icon={<CloseIcon data-testid="x-close-icon" />}
                variant="fill"
              />
            ) : null}
          </div>
        </>
      )}
    </MuiDialogTitle>
  );
};

const BDDialog = <T,>({
  openModal,
  onCloseModal,
  component: DialogComponent,
  dialogSize,
  disableSubmit,
  disableClose,
  entity,
  entityId,
  title,
  onResult,
  buttonsConfig,
  customActionsContent,
  useLegacyAssign,
  disableEntityFields,
  labels,
  extendedMobileView,
  drawerMobileView,
  containedContentScroll = false,
  className,
  dataTestId,
  disableBackdropClick = false,
  onExited,
}: DialogWrapperProps<T>): JSX.Element => {
  const { classes } = useStyles();
  const errorScrollViewRef = useRef<HTMLInputElement>(null);
  const [dialogAlerts, setDialogAlerts] = useState<BDAlert[]>([]);
  const [alertBanners, setAlertBanners] = useState<AlertBannerProps[]>([]);

  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const closeModal = useCallback(
    (success: boolean) => {
      setDialogAlerts([]);
      setAlertBanners([]);
      onCloseModal(success);
    },
    [onCloseModal]
  );

  const handleSetAlerts = useCallback(
    (
      newAlerts: BDAlert[] | ((existingAlerts: BDAlert[]) => BDAlert[]),
      replace = true
    ) => {
      setDialogAlerts((dialogAlerts) => {
        const alerts =
          typeof newAlerts === 'function' ? newAlerts(dialogAlerts) : newAlerts;
        return replace ? [...alerts] : [...dialogAlerts, ...alerts];
      });
      if (errorScrollViewRef.current) {
        errorScrollViewRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    []
  );

  const handleSetAlertBanners = useCallback(
    (
      newAlerts:
        | AlertBannerProps[]
        | ((existingAlerts: AlertBannerProps[]) => AlertBannerProps[]),
      replace = true
    ) => {
      setAlertBanners((alertBanners) => {
        const alerts =
          typeof newAlerts === 'function' ? newAlerts(alertBanners) : newAlerts;
        return replace ? [...alerts] : [...alertBanners, ...alerts];
      });
      if (errorScrollViewRef.current) {
        errorScrollViewRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    []
  );

  const handleModalClose = useCallback(
    (reason?: ModalCloseReason) => {
      if (reason === 'backdropClick' && disableBackdropClick) {
        return;
      }

      closeModal(false);
    },
    [disableBackdropClick, closeModal]
  );

  // Render drawer for small mobile viewports
  if (drawerMobileView && isSmallMobile) {
    return (
      <BDBottomSheet
        open={openModal}
        onClose={handleModalClose}
        showBackdrop={true}
        ariaLabels={{
          panel: labels?.aria,
          dragHandle: labels?.dragHandle,
        }}
        onExited={onExited}
      >
        <Box className={classes.drawerContent}>
          {/* Drawer Title */}
          {title && (
            <Typography
              className={classes.drawerTitle}
              variant="header1_medium"
            >
              {title}
            </Typography>
          )}

          {/* Drawer Content */}
          <Box
            className={
              containedContentScroll
                ? classes.drawerScrollContentContained
                : classes.drawerScrollContent
            }
          >
            <div id="errorScrollView" ref={errorScrollViewRef} />

            {/* Render BDAlerts if present */}
            {dialogAlerts && dialogAlerts.length > 0 && (
              <BDAlerts alerts={dialogAlerts} />
            )}

            {/* Render AlertBanner if present */}
            {alertBanners && alertBanners.length > 0 && (
              <Box mb={2}>
                <AlertBanner {...alertBanners[0]} />
              </Box>
            )}

            {containedContentScroll ? (
              <Box
                className={classes.containedContentWrapper}
                data-testid="dialog-contained-content"
              >
                <DialogComponent
                  onCloseModal={closeModal}
                  entity={entity}
                  entityId={entityId}
                  disableSubmit={disableSubmit}
                  setAlerts={handleSetAlerts}
                  setAlertBanners={handleSetAlertBanners}
                  onResult={onResult}
                  useLegacyAssign={useLegacyAssign}
                  disableEntityFields={disableEntityFields}
                />
              </Box>
            ) : (
              <DialogComponent
                onCloseModal={closeModal}
                entity={entity}
                entityId={entityId}
                disableSubmit={disableSubmit}
                setAlerts={handleSetAlerts}
                setAlertBanners={handleSetAlertBanners}
                onResult={onResult}
                useLegacyAssign={useLegacyAssign}
                disableEntityFields={disableEntityFields}
              />
            )}
          </Box>

          {/* Drawer Actions */}
          {buttonsConfig && (
            <Box className={classes.drawerActions}>
              <ActionButtons
                buttons={buttonsConfig}
                customActionsContent={customActionsContent}
              />
            </Box>
          )}
        </Box>
      </BDBottomSheet>
    );
  }

  // Standard dialog for desktop and larger mobile viewports
  return (
    <>
      <Dialog
        data-testid={dataTestId}
        id="dialog-form"
        open={openModal}
        aria-label={labels?.aria}
        className={cx(
          { [classes.extendedMobileView]: extendedMobileView },
          { [classes.containedContentScrollDialog]: containedContentScroll },
          className
        )}
        fullWidth={true}
        fullScreen={extendedMobileView && isMobileView}
        maxWidth={dialogSize || 'md'}
        scroll="paper"
        onClose={(_event, reason) => handleModalClose(reason)}
        slotProps={{ transition: { onExited } }}
      >
        <div id="errorScrollView" ref={errorScrollViewRef} />
        {title && (
          <DialogTitle
            id="dialog-title"
            title={title}
            onClose={() => closeModal(false)}
            data-testid="dialog-title"
            disableClose={disableClose}
            labels={labels}
            extendedMobile={extendedMobileView}
          />
        )}
        <DialogContent className={classes.dialogContent}>
          {/* Render BDAlerts if present */}
          {dialogAlerts && dialogAlerts.length > 0 && (
            <BDAlerts alerts={dialogAlerts} />
          )}

          {/* Render AlertBanner if present */}
          {alertBanners && alertBanners.length > 0 && (
            <Box mb={2}>
              <AlertBanner {...alertBanners[0]} />
            </Box>
          )}

          {containedContentScroll ? (
            <Box
              className={classes.containedContentWrapper}
              data-testid="dialog-contained-content"
            >
              <DialogComponent
                onCloseModal={closeModal}
                entity={entity}
                entityId={entityId}
                disableSubmit={disableSubmit}
                setAlerts={handleSetAlerts}
                setAlertBanners={handleSetAlertBanners}
                onResult={onResult}
                useLegacyAssign={useLegacyAssign}
                disableEntityFields={disableEntityFields}
              />
            </Box>
          ) : (
            <DialogComponent
              onCloseModal={closeModal}
              entity={entity}
              entityId={entityId}
              disableSubmit={disableSubmit}
              setAlerts={handleSetAlerts}
              setAlertBanners={handleSetAlertBanners}
              onResult={onResult}
              useLegacyAssign={useLegacyAssign}
              disableEntityFields={disableEntityFields}
            />
          )}
        </DialogContent>
        {buttonsConfig && (
          <DialogActions className={classes.dialogActions}>
            <ActionButtons
              buttons={buttonsConfig}
              customActionsContent={customActionsContent}
            />
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
export default BDDialog;
